import { useState, useMemo, useRef, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency, formatDate } from '../../utils/format';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEDUCTION_COLORS = {
  federalTax: '#EF4444',
  stateTax: '#F97316',
  socialSecurity: '#FBBF24',
  medicare: '#A3E635',
  retirement401k: '#34D399',
  hsa: '#22D3EE',
  healthInsurance: '#818CF8',
  otherDeductions: '#A78BFA',
};

const DEDUCTION_LABELS = {
  federalTax: 'Federal Income Tax',
  stateTax: 'State Income Tax',
  socialSecurity: 'Social Security',
  medicare: 'Medicare',
  retirement401k: '401(k)',
  hsa: 'HSA',
  healthInsurance: 'Health Insurance',
  otherDeductions: 'Other Pre-tax',
};

const DEDUCTION_KEYS = [
  'federalTax',
  'stateTax',
  'socialSecurity',
  'medicare',
  'retirement401k',
  'hsa',
  'healthInsurance',
  'otherDeductions',
];

const EMPTY_FORM = {
  payDate: '',
  payPeriodStart: '',
  payPeriodEnd: '',
  employer: '',
  grossPay: '',
  federalTax: '',
  stateTax: '',
  socialSecurity: '',
  medicare: '',
  retirement401k: '',
  hsa: '',
  healthInsurance: '',
  otherDeductions: '',
  otherDeductionsLabel: '',
  notes: '',
};

const DEFAULT_PAYCHECK = {
  grossPay: 0,
  federalTax: 0,
  stateTax: 0,
  socialSecurity: 0,
  medicare: 0,
  retirement401k: 0,
  hsa: 0,
  healthInsurance: 0,
  otherDeductions: 0,
  netPay: 0,
  employer: '',
};

function getDeductions(paycheck) {
  return DEDUCTION_KEYS.reduce((sum, key) => sum + (paycheck[key] || 0), 0);
}

// ─── Tooltip Components ──────────────────────────────────────────────────────

function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const data = payload[0];
  return (
    <div className="bg-bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-text-primary text-sm font-medium">{data.name}</p>
      <p className="text-text-secondary text-xs">
        {formatCurrency(data.value, 2)} ({((data.value / data.payload.total) * 100).toFixed(1)}%)
      </p>
    </div>
  );
}

const inputClass =
  'w-full bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent';

// Helper: get previous month key from YYYY-MM
function prevMonthKey(key) {
  const [y, m] = key.split('-').map(Number);
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return `${py}-${String(pm).padStart(2, '0')}`;
}

// ─── Recurring Postings Config ───────────────────────────────────────────────

const POSTING_CATEGORIES = ['Rent', 'Car', 'Utilities', 'Insurance', 'Loan', 'Salary', 'Side Income', 'Other'];

function RecurringPostingsConfig({ postings, accounts, onSave }) {
  const [expanded, setExpanded] = useState(false);

  const addPosting = () => {
    onSave([...postings, {
      id: crypto.randomUUID(),
      description: '',
      amount: 0,
      dayOfMonth: 1,
      category: 'Other',
      type: 'expense',
      account: '',
      accountId: null,
      accountType: 'account',
      enabled: true,
    }]);
  };

  const updatePosting = (id, updates) => {
    onSave(postings.map((p) => p.id === id ? { ...p, ...updates } : p));
  };

  const removePosting = (id) => {
    onSave(postings.filter((p) => p.id !== id));
  };

  return (
    <Card className="section-gap">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Recurring Postings</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">{postings.length} rules</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-text-muted">
            Define monthly recurring transactions (rent, car payment, etc.) that auto-post on app load.
          </p>

          {postings.map((posting) => (
            <div key={posting.id} className="flex flex-wrap items-center gap-2 p-3 bg-bg-tertiary rounded-lg border border-border/30">
              <input
                type="text"
                placeholder="Description"
                value={posting.description}
                onChange={(e) => updatePosting(posting.id, { description: e.target.value })}
                className="flex-1 min-w-[120px] text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
              />
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={posting.amount || ''}
                onChange={(e) => updatePosting(posting.id, { amount: parseFloat(e.target.value) || 0 })}
                className="w-24 text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary font-mono focus:outline-none focus:border-accent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <select
                value={posting.type}
                onChange={(e) => updatePosting(posting.id, { type: e.target.value })}
                className="text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
              <select
                value={posting.category}
                onChange={(e) => updatePosting(posting.id, { category: e.target.value })}
                className="text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
              >
                {POSTING_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <span className="text-xs text-text-muted">Day</span>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={posting.dayOfMonth}
                  onChange={(e) => updatePosting(posting.id, { dayOfMonth: parseInt(e.target.value) || 1 })}
                  className="w-14 text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary font-mono focus:outline-none focus:border-accent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <select
                value={posting.accountId || ''}
                onChange={(e) => {
                  const acct = accounts.find((a) => a.id === e.target.value);
                  updatePosting(posting.id, {
                    accountId: e.target.value || null,
                    account: acct?.name || '',
                    accountType: 'account',
                  });
                }}
                className="text-xs bg-bg-card border border-border/50 rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
              >
                <option value="">No account</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label className="flex items-center gap-1 text-xs text-text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={posting.enabled !== false}
                  onChange={(e) => updatePosting(posting.id, { enabled: e.target.checked })}
                  className="rounded"
                />
                On
              </label>
              <button
                onClick={() => removePosting(posting.id)}
                className="p-1 rounded hover:bg-danger/20 text-text-muted hover:text-danger transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <button
            onClick={addPosting}
            className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add recurring posting
          </button>
        </div>
      )}
    </Card>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

// ─── Inline-editable budget cell ─────────────────────────────────────────────
function BudgetEditCell({ value, onSave, prefix = '$', suffix = '', align = 'right' }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = Number(editVal);
    if (!isNaN(num) && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td align={align}>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
          }}
          className="w-24 bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary font-mono text-right focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      align={align}
      className="font-mono tabular-nums cursor-pointer hover:bg-accent/10 transition-colors text-danger"
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      -{prefix}{formatCurrency(value, 2).replace('$', '')}{suffix}
    </Td>
  );
}

// ─── Inline brokerage % editor ───────────────────────────────────────────────
function BrokeragePctInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = Number(editVal);
    if (!isNaN(num) && num >= 0 && num <= 100 && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        max="100"
        step="1"
        value={editVal}
        onChange={(e) => setEditVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
        }}
        className="w-12 bg-bg-tertiary border border-accent rounded px-1 py-0.5 text-xs text-text-primary font-mono text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        autoFocus
      />
    );
  }

  return (
    <span
      className="text-xs text-accent cursor-pointer hover:underline font-mono"
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit split %"
    >
      {value}%
    </span>
  );
}

export default function PaycheckSnapshot() {
  const paycheckSnapshots = useStore((s) => s.paycheckSnapshots);
  const addPaycheckSnapshot = useStore((s) => s.addPaycheckSnapshot);
  const removePaycheckSnapshot = useStore((s) => s.removePaycheckSnapshot);
  const budgetConfig = useStore((s) => s.userSettings.budgetConfig);
  const updateBudgetConfig = useStore((s) => s.updateBudgetConfig);
  const accounts = useStore((s) => s.accounts);
  const subscriptions = useStore((s) => s.subscriptions);

  const payFrequency = budgetConfig?.payFrequency || 'semi-monthly';
  const transactions = useStore((s) => s.transactions);
  const creditCards = useStore((s) => s.creditCards);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);

  // Monthly subscription cost (same calc as Subscriptions page)
  const monthlySubscriptions = useMemo(() => {
    const normalizeFreq = (f) => {
      if (typeof f === 'number') return f;
      if (f === 'Monthly') return 12;
      if (f === 'Annual') return 1;
      return parseInt(f, 10) || 12;
    };
    const annual = subscriptions.reduce((sum, s) => sum + (s.cost || 0) * normalizeFreq(s.frequency), 0);
    return Math.round((annual / 12) * 100) / 100;
  }, [subscriptions]);

  // Current month CC Spend & adjusted outflow (from Cashflow data)
  const currentMonthCashflow = useMemo(() => {
    const now = new Date();
    const curMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Filter current month transactions (string-based to avoid UTC timezone shift)
    const curMonthTxns = (transactions || []).filter((tx) => {
      const key = typeof tx.date === 'string' ? tx.date.substring(0, 7)
        : `${new Date(tx.date).getFullYear()}-${String(new Date(tx.date).getMonth() + 1).padStart(2, '0')}`;
      return key === curMonthKey;
    });
    const curMonthExpenses = curMonthTxns.filter((tx) => tx.type === 'expense');

    const cashOutflow = curMonthExpenses.reduce((s, t) => s + (t.amount || 0), 0);
    const ccPayments = curMonthExpenses
      .filter((t) => t.category === 'CC Payment')
      .reduce((s, t) => s + (t.amount || 0), 0);
    const reimbursements = curMonthTxns
      .filter((t) => t.type === 'income' && t.category === 'Reimbursement')
      .reduce((s, t) => s + (t.amount || 0), 0);

    // Live CC balance
    const liveCCBalance = (creditCards || []).reduce(
      (sum, c) => sum + Math.abs(c.balance || 0), 0
    );

    // Previous month CC balance from balanceSheetMonthly
    const prevKey = prevMonthKey(curMonthKey);
    const prevRow = (balanceSheetMonthly || []).find((r) => r.month === prevKey);
    const prevBal = prevRow ? (prevRow.creditCards || 0) : 0;

    // CC Spend = MAX(0, liveCCBalance - prevBal + ccPayments)
    const ccSpend = Math.max(0, liveCCBalance - prevBal + ccPayments);

    return { ccSpend, cashOutflow, reimbursements };
  }, [transactions, creditCards, balanceSheetMonthly]);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Sorted paychecks (descending by payDate)
  const sorted = useMemo(
    () => [...paycheckSnapshots].sort((a, b) => new Date(b.payDate) - new Date(a.payDate)),
    [paycheckSnapshots],
  );

  const latest = sorted[0] || null;
  // Use the latest recorded paycheck if available, otherwise fall back to defaults
  const baseline = latest || DEFAULT_PAYCHECK;

  // ─── Paycheck / Monthly / Annual Breakdown ──────────────────────────────

  const breakdown = useMemo(() => {
    const src = baseline;

    const perPaycheck = {
      grossPay: src.grossPay || 0,
      federalTax: src.federalTax || 0,
      stateTax: src.stateTax || 0,
      socialSecurity: src.socialSecurity || 0,
      medicare: src.medicare || 0,
      retirement401k: src.retirement401k || 0,
      hsa: src.hsa || 0,
      healthInsurance: src.healthInsurance || 0,
      otherDeductions: src.otherDeductions || 0,
      netPay: src.netPay || 0,
    };

    const totalDeductions = getDeductions(perPaycheck);
    const taxRate = perPaycheck.grossPay > 0 ? (totalDeductions / perPaycheck.grossPay) * 100 : 0;

    // Per-paycheck → monthly/annual using the actual pay frequency.
    const checksPerYear = payFrequency === 'biweekly' ? 26 : 24;
    const monthly = {};
    const annual = {};
    for (const key of ['grossPay', ...DEDUCTION_KEYS, 'netPay']) {
      monthly[key] = perPaycheck[key] * (checksPerYear / 12);
      annual[key] = perPaycheck[key] * checksPerYear;
    }

    return { perPaycheck, monthly, annual, totalDeductions, taxRate };
  }, [baseline, payFrequency]);

  // ─── Monthly Budget Waterfall ─────────────────────────────────────────

  const budget = useMemo(() => {
    if (!breakdown) return null;
    const m = breakdown.monthly;
    const grossRevenue = m.grossPay;
    const taxes = (m.federalTax || 0) + (m.stateTax || 0) + (m.socialSecurity || 0) + (m.medicare || 0);
    const benefits = (m.healthInsurance || 0) + (m.otherDeductions || 0) + (m.retirement401k || 0) + (m.hsa || 0);
    const netRevenue = grossRevenue - taxes - benefits;

    const { rothIra = 0, roth401k = 0, hsa = 0, lifeInsurance = 0, rent = 0, carPayment = 0 } = budgetConfig || {};
    const totalAllocations = rothIra + roth401k + hsa + lifeInsurance + rent + carPayment + monthlySubscriptions;
    const unallocated = netRevenue - totalAllocations;

    return {
      grossRevenue, taxes, benefits, netRevenue, rothIra, roth401k, hsa, lifeInsurance, rent, carPayment, monthlySubscriptions, unallocated,
    };
  }, [breakdown, budgetConfig, monthlySubscriptions]);

  // ─── Donut chart data ───────────────────────────────────────────────────

  const donutData = useMemo(() => {
    const totalDeductions = getDeductions(baseline);
    const slices = DEDUCTION_KEYS.filter((key) => (baseline[key] || 0) > 0).map((key) => ({
      name: DEDUCTION_LABELS[key],
      value: baseline[key] || 0,
      color: DEDUCTION_COLORS[key],
      total: totalDeductions,
    }));
    return { slices, totalDeductions };
  }, [baseline]);

  // ─── Form helpers ───────────────────────────────────────────────────────

  const numVal = (v) => (v === '' ? 0 : parseFloat(v) || 0);

  const formNetPay = useMemo(() => {
    const gross = numVal(form.grossPay);
    const deductions = DEDUCTION_KEYS.reduce((sum, key) => sum + numVal(form[key]), 0);
    return gross - deductions;
  }, [form]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.payDate || !form.grossPay) return;
    const gross = numVal(form.grossPay);
    if (isNaN(gross) || gross <= 0) return;

    const snapshot = {
      payDate: form.payDate,
      payPeriodStart: form.payPeriodStart || null,
      payPeriodEnd: form.payPeriodEnd || null,
      employer: form.employer || null,
      grossPay: gross,
      federalTax: numVal(form.federalTax),
      stateTax: numVal(form.stateTax),
      socialSecurity: numVal(form.socialSecurity),
      medicare: numVal(form.medicare),
      retirement401k: numVal(form.retirement401k),
      hsa: numVal(form.hsa),
      healthInsurance: numVal(form.healthInsurance),
      otherDeductions: numVal(form.otherDeductions),
      otherDeductionsLabel: form.otherDeductionsLabel || null,
      netPay: formNetPay,
      notes: form.notes || null,
    };

    addPaycheckSnapshot(snapshot);
    setForm(EMPTY_FORM);
    setShowAdd(false);
  };

  // ─── Breakdown row helper ──────────────────────────────────────────────

  // Build row items dynamically — only show deductions that have a value
  const ROW_ITEMS = useMemo(() => {
    const deductionRows = DEDUCTION_KEYS
      .filter((key) => {
        // Show if latest paycheck has this deduction OR ytd has it
        if (!breakdown) return false;
        return (breakdown.perPaycheck[key] || 0) > 0;
      })
      .map((key) => ({ key, label: DEDUCTION_LABELS[key], indent: true }));

    return [
      { key: 'grossPay', label: 'Gross Pay', bold: true },
      ...deductionRows,
      { key: 'netPay', label: 'Net Pay', bold: true, highlight: true },
    ];
  }, [breakdown]);

  return (
    <div>
      <PageHeader
        title="Paycheck"
        subtitle={`${latest ? `Latest: ${formatDate(latest.payDate)} · ` : ''}${payFrequency === 'biweekly' ? 'Biweekly' : 'Semi-monthly'} · ${baseline.employer || ''}`}
      >
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted whitespace-nowrap" title="If set, no paychecks are auto-posted past this date.">
            Last paycheck
          </label>
          <input
            type="date"
            value={budgetConfig?.paycheckEndDate || ''}
            onChange={(e) => updateBudgetConfig({ paycheckEndDate: e.target.value || null })}
            className="text-xs bg-bg-tertiary border border-border rounded-md px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
          />
          {budgetConfig?.paycheckEndDate && (
            <button
              onClick={() => updateBudgetConfig({ paycheckEndDate: null })}
              className="text-xs text-text-muted hover:text-text-primary"
              title="Clear last-paycheck date"
            >
              Clear
            </button>
          )}
          <select
            value={payFrequency}
            onChange={(e) => updateBudgetConfig({ payFrequency: e.target.value })}
            className="text-xs bg-bg-tertiary border border-border rounded-md px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
          >
            <option value="semi-monthly">Semi-monthly</option>
            <option value="biweekly">Biweekly</option>
          </select>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-accent text-white hover:bg-accent/90 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </PageHeader>

      {/* ── Paycheck Account Selector ──────────────────────────────────────── */}
      <Card className="section-gap">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-xs text-text-muted block mb-1">Paycheck deposits to</label>
            <select
              value={budgetConfig?.paycheckAccountId || ''}
              onChange={(e) => {
                const acct = accounts.find((a) => a.id === e.target.value);
                updateBudgetConfig({
                  paycheckAccountId: e.target.value || null,
                  paycheckAccountName: acct?.name || 'Checking',
                  paycheckAccountType: 'account',
                });
              }}
              className="text-sm bg-bg-tertiary border border-border rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:border-accent w-full"
            >
              <option value="">Not set</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({formatCurrency(a.balance)})</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* ── Monthly Budget Waterfall ────────────────────────────────────────── */}
      {budget && (
        <Card className="section-gap">
          <h3 className="text-sm font-semibold text-text-primary mb-4">Monthly Budget</h3>
          <Table>
            <Thead>
              <tr>
                <Th>Category</Th>
                <Th align="right">Budget</Th>
                <Th align="right">Actual</Th>
                <Th align="right">Annual</Th>
              </tr>
            </Thead>
            <tbody>
              {/* Gross Revenue */}
              <Tr>
                <Td className="font-semibold text-text-primary">Gross Revenue</Td>
                <Td align="right" className="font-mono font-semibold text-text-primary">{formatCurrency(budget.grossRevenue, 2)}</Td>
                <Td align="right" className="font-mono text-text-primary">{formatCurrency(budget.grossRevenue, 2)}</Td>
                <Td align="right" className="font-mono text-text-secondary">{formatCurrency(budget.grossRevenue * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Taxes</Td>
                <Td align="right" className="font-mono text-danger">-{formatCurrency(budget.taxes, 2)}</Td>
                <Td align="right" className="font-mono text-danger">-{formatCurrency(budget.taxes, 2)}</Td>
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.taxes * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Benefits</Td>
                <Td align="right" className="font-mono text-danger">-{formatCurrency(budget.benefits, 2)}</Td>
                <Td align="right" className="font-mono text-danger">-{formatCurrency(budget.benefits, 2)}</Td>
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.benefits * 12, 0)}</Td>
              </Tr>

              {/* Net Revenue */}
              <Tr>
                <Td className="font-semibold text-text-primary">Net Revenue</Td>
                <Td align="right" className="font-mono font-semibold text-success">{formatCurrency(budget.netRevenue, 2)}</Td>
                <Td align="right" className="font-mono font-semibold text-success">{formatCurrency(budget.netRevenue, 2)}</Td>
                <Td align="right" className="font-mono text-success/60">{formatCurrency(budget.netRevenue * 12, 0)}</Td>
              </Tr>

              {/* Contributions */}
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />IRA</Td>
                <BudgetEditCell value={budget.rothIra} onSave={(v) => updateBudgetConfig({ rothIra: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.rothIra * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Roth 401(k)</Td>
                <BudgetEditCell value={budget.roth401k} onSave={(v) => updateBudgetConfig({ roth401k: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.roth401k * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />HSA</Td>
                <BudgetEditCell value={budget.hsa} onSave={(v) => updateBudgetConfig({ hsa: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.hsa * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Life Insurance</Td>
                <BudgetEditCell value={budget.lifeInsurance} onSave={(v) => updateBudgetConfig({ lifeInsurance: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.lifeInsurance * 12, 0)}</Td>
              </Tr>

              {/* Fixed Expenses */}
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Rent</Td>
                <BudgetEditCell value={budget.rent} onSave={(v) => updateBudgetConfig({ rent: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.rent * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Car Payment</Td>
                <BudgetEditCell value={budget.carPayment} onSave={(v) => updateBudgetConfig({ carPayment: v })} />
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.carPayment * 12, 0)}</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Subscriptions</Td>
                <Td align="right" className="font-mono tabular-nums text-danger">-${formatCurrency(budget.monthlySubscriptions, 2).replace('$', '')}</Td>
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger/60">-{formatCurrency(budget.monthlySubscriptions * 12, 0)}</Td>
              </Tr>

              {/* Unallocated */}
              <Tr>
                <Td className="font-semibold text-text-primary">Unallocated</Td>
                <Td align="right" className="font-mono font-semibold text-accent">{formatCurrency(budget.unallocated, 2)}</Td>
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-accent/60">{formatCurrency(budget.unallocated * 12, 0)}</Td>
              </Tr>

              {/* CC Spend & Outflow */}
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />CC Spend</Td>
                <Td align="right" className="font-mono text-danger">-{formatCurrency(currentMonthCashflow.ccSpend, 2)}</Td>
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-text-muted">—</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Reimbursements</Td>
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-success">{formatCurrency(currentMonthCashflow.reimbursements, 2)}</Td>
                <Td align="right" className="font-mono text-text-muted">—</Td>
              </Tr>
              <Tr>
                <Td className="text-text-secondary"><span className="ml-4" />Outflow</Td>
                <Td align="right" className="font-mono text-text-muted" />
                <Td align="right" className="font-mono text-danger">-{formatCurrency(currentMonthCashflow.cashOutflow, 2)}</Td>
                <Td align="right" className="font-mono text-text-muted">—</Td>
              </Tr>

              {/* Net Spend / Net Outflow */}
              {(() => {
                const netSpend = budget.unallocated - currentMonthCashflow.ccSpend;
                const netOutflow = budget.netRevenue + currentMonthCashflow.reimbursements - currentMonthCashflow.cashOutflow;
                return (
                  <Tr>
                    <Td className="font-semibold text-text-primary" />
                    <Td align="right" className={`font-mono font-semibold ${netSpend >= 0 ? 'text-success' : 'text-danger'}`}>
                      <span className="block text-[10px] text-text-muted uppercase tracking-wide">Net Spend</span>
                      {formatCurrency(netSpend, 2)}
                    </Td>
                    <Td align="right" className={`font-mono font-semibold ${netOutflow >= 0 ? 'text-success' : 'text-danger'}`}>
                      <span className="block text-[10px] text-text-muted uppercase tracking-wide">Net Outflow</span>
                      {formatCurrency(netOutflow, 2)}
                    </Td>
                    <Td align="right" className="font-mono text-text-muted">—</Td>
                  </Tr>
                );
              })()}
            </tbody>
          </Table>
        </Card>
      )}

      {/* ── Paycheck / Monthly / Annual Breakdown Table ───────────────────── */}
      {breakdown && (
        <Card className="section-gap">
          <h3 className="text-sm font-semibold text-text-primary mb-4">Pay Breakdown</h3>
          <Table>
            <Thead>
              <tr>
                <Th>Category</Th>
                <Th align="right">Per Paycheck</Th>
                <Th align="right">Monthly</Th>
                <Th align="right">Annual</Th>
              </tr>
            </Thead>
            <tbody>
              {ROW_ITEMS.map((item) => {
                const isDeduction = item.indent;
                return (
                  <Tr key={item.key}>
                    <Td className={item.bold ? 'font-semibold text-text-primary' : 'text-text-secondary'}>
                      {item.indent && <span className="ml-4" />}
                      {item.label}
                    </Td>
                    <Td
                      align="right"
                      className={`font-mono ${
                        item.highlight
                          ? 'text-success font-semibold'
                          : item.bold
                            ? 'text-text-primary font-semibold'
                            : isDeduction
                              ? 'text-danger'
                              : 'text-text-primary'
                      }`}
                    >
                      {isDeduction ? `-${formatCurrency(breakdown.perPaycheck[item.key], 2)}` : formatCurrency(breakdown.perPaycheck[item.key], 2)}
                    </Td>
                    <Td
                      align="right"
                      className={`font-mono ${
                        item.highlight
                          ? 'text-success font-semibold'
                          : item.bold
                            ? 'text-text-primary font-semibold'
                            : isDeduction
                              ? 'text-danger'
                              : 'text-text-primary'
                      }`}
                    >
                      {isDeduction ? `-${formatCurrency(breakdown.monthly[item.key], 2)}` : formatCurrency(breakdown.monthly[item.key], 2)}
                    </Td>
                    <Td
                      align="right"
                      className={`font-mono ${
                        item.highlight
                          ? 'text-success font-semibold'
                          : item.bold
                            ? 'text-text-primary font-semibold'
                            : isDeduction
                              ? 'text-danger'
                              : 'text-text-primary'
                      }`}
                    >
                      {isDeduction ? `-${formatCurrency(breakdown.annual[item.key], 2)}` : formatCurrency(breakdown.annual[item.key], 2)}
                    </Td>
                  </Tr>
                );
              })}
              {/* Effective tax rate row */}
              <Tr>
                <Td className="text-text-secondary italic">Effective Tax Rate</Td>
                <Td align="right" className="font-mono text-text-secondary italic">
                  {breakdown.taxRate.toFixed(1)}%
                </Td>
                <Td align="right" className="font-mono text-text-secondary italic">
                  {breakdown.taxRate.toFixed(1)}%
                </Td>
                <Td align="right" className="font-mono text-text-secondary italic">
                  {breakdown.taxRate.toFixed(1)}%
                </Td>
              </Tr>
            </tbody>
          </Table>
        </Card>
      )}

      {/* ── Deduction Donut + KPI Summary ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 kpi-grid">
        {/* Deduction Donut */}
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-4">Deduction Breakdown</h3>
          {donutData.slices.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData.slices}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {donutData.slices.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#8B949E' }} />
                  <text
                    x="50%"
                    y="47%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-text-secondary"
                    style={{ fontSize: 11 }}
                  >
                    Total
                  </text>
                  <text
                    x="50%"
                    y="55%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-text-primary"
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {formatCurrency(donutData.totalDeductions, 2)}
                  </text>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-text-muted text-sm text-center py-8">No deduction data available.</p>
          )}
        </Card>

        {/* Key Figures */}
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-4">Key Figures</h3>
          <div className="space-y-4">
            {[
              { label: 'Take-home per paycheck', value: breakdown.perPaycheck.netPay || 0, color: 'text-success' },
              { label: 'Monthly take-home', value: breakdown.monthly.netPay || 0, color: 'text-success' },
              { label: 'Annual take-home (est.)', value: breakdown.annual.netPay || 0, color: 'text-success' },
              { label: 'Annual gross salary', value: breakdown.annual.grossPay || 0, color: 'text-text-primary' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{item.label}</span>
                <span className={`text-lg font-semibold font-mono ${item.color}`}>
                  {formatCurrency(item.value, item.value >= 1000 ? 0 : 2)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Pay History Table ─────────────────────────────────────────────── */}
      <Card className="section-gap">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Pay History</h3>
        <Table>
          <Thead>
            <tr>
              <Th>Pay Date</Th>
              <Th>Period</Th>
              <Th align="right">Gross Pay</Th>
              <Th align="right">Deductions</Th>
              <Th align="right">Net Pay</Th>
              <Th align="center">Actions</Th>
            </tr>
          </Thead>
          <tbody>
            {sorted.map((paycheck) => {
              const totalDed = getDeductions(paycheck);
              const period =
                paycheck.payPeriodStart && paycheck.payPeriodEnd
                  ? `${formatDate(paycheck.payPeriodStart)} – ${formatDate(paycheck.payPeriodEnd)}`
                  : '—';
              return (
                <Tr key={paycheck.id}>
                  <Td className="text-text-primary font-medium">{formatDate(paycheck.payDate)}</Td>
                  <Td className="text-text-secondary text-xs">{period}</Td>
                  <Td align="right" className="font-mono text-text-primary">
                    {formatCurrency(paycheck.grossPay, 2)}
                  </Td>
                  <Td align="right" className="font-mono text-danger">
                    -{formatCurrency(totalDed, 2)}
                  </Td>
                  <Td align="right" className="font-mono text-success font-semibold">
                    {formatCurrency(paycheck.netPay, 2)}
                  </Td>
                  <Td align="center">
                    <button
                      onClick={() => removePaycheckSnapshot(paycheck.id)}
                      className="p-1 rounded hover:bg-danger/20 text-text-muted hover:text-danger transition-colors"
                      title="Delete paycheck"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
        {sorted.length === 0 && (
          <p className="text-text-muted text-sm text-center py-8">
            No paychecks recorded yet. Add your first paycheck above.
          </p>
        )}
      </Card>

      {/* ── Recurring Postings Config ──────────────────────────────────────── */}
      <RecurringPostingsConfig
        postings={budgetConfig?.recurringPostings || []}
        accounts={accounts}
        onSave={(postings) => updateBudgetConfig({ recurringPostings: postings })}
      />

      {/* ── Add Paycheck Form ─────────────────────────────────────────────── */}
      <Card>
        <button onClick={() => setShowAdd(!showAdd)} className="w-full flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Add Paycheck</h3>
          {showAdd ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
        </button>

        {showAdd && (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Row 1: Dates & Employer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Pay Date *</label>
                <input type="date" required value={form.payDate} onChange={(e) => updateField('payDate', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Period Start</label>
                <input type="date" value={form.payPeriodStart} onChange={(e) => updateField('payPeriodStart', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Period End</label>
                <input type="date" value={form.payPeriodEnd} onChange={(e) => updateField('payPeriodEnd', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Employer</label>
                <input type="text" placeholder="Company name" value={form.employer} onChange={(e) => updateField('employer', e.target.value)} className={inputClass} />
              </div>
            </div>

            {/* Row 2: Gross Pay */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Gross Pay *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  value={form.grossPay}
                  onChange={(e) => updateField('grossPay', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Row 3: Deductions */}
            <div>
              <p className="text-xs text-text-secondary font-medium uppercase tracking-wider mb-2">Deductions</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  { key: 'federalTax', label: 'Federal Income Tax' },
                  { key: 'stateTax', label: 'State Income Tax' },
                  { key: 'socialSecurity', label: 'Social Security' },
                  { key: 'medicare', label: 'Medicare' },
                  { key: 'retirement401k', label: '401(k)' },
                  { key: 'hsa', label: 'HSA' },
                  { key: 'healthInsurance', label: 'Health Insurance' },
                  { key: 'otherDeductions', label: 'Other Pre-tax' },
                ].map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs text-text-secondary mb-1">{field.label}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={form[field.key]}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Row 4: Other label, notes, net pay */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Other Deductions Label</label>
                <input
                  type="text"
                  placeholder="e.g. Dental, Vision"
                  value={form.otherDeductionsLabel}
                  onChange={(e) => updateField('otherDeductionsLabel', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Notes</label>
                <input type="text" placeholder="Optional notes" value={form.notes} onChange={(e) => updateField('notes', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Net Pay (auto-calculated)</label>
                <div className="bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm font-mono text-success font-semibold">
                  {formatCurrency(formNetPay, 2)}
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-2">
              <button type="submit" className="bg-accent text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-accent-hover transition-colors">
                Add Paycheck
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
